export const parseJWt = (token:string) =>{
    if(token) {
        var base64Url = token.split('.')[1];
        var base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        var jsonPayload = decodeURIComponent(window.atob(base64).split('').map(function(c) {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        }).join(''));
    
        return JSON.parse(jsonPayload);
    }
    return null;
}

export type CustomField = {
    id: string,
    nombre: string,
    tipo_campo: string,
    mandatorio: string,
    tipo_formulario: string,
    attribute: string,
    elemento_lista_desplegable: string
};

export const parseJsonStringToArray = (data: null | string):any[] => {
    if(data){
        try {
            return JSON.parse(data);
        } catch (error) {
            console.log(error);
        }
    }
    return [];
}

export const hasTypeForm = (data: null | string, typeForm: string):boolean => {
    if(data){
        try {
            const arrayData = JSON.parse(data);
            // Verifica si al menos un objeto en el array tiene el typeForm especificado
            return arrayData.some((item: { tipo_formulario: string }) => item.tipo_formulario === typeForm);
        } catch (error) {
            console.log(error);
        }
    }
    return false;
}

export const parseCustomFields = (data: null | string, typeForm: string) => {
    if(data){
        try {
            //parseo
            const customFields: CustomField[] = JSON.parse(data);
            //filtro los que me importan para el tipo de formulario
            const currentCustomFields = customFields.filter(customField => customField.tipo_formulario === typeForm);
            
            //transformo los filtrados en un array
            const arrayCustomFields: string[] = currentCustomFields.map(customField => {
                return customField.attribute;
            });

            //luego transformo todo en un objeto que sera devuelto para extrapolar en el formulario
            return arrayCustomFields.reduce((acc, current) => {
                acc[current] = '';
                return acc;
              }, {} as { [key: string]: string });
        } catch (error) {
            console.log(error);
        }
    }
    return null;
}

const hasRequiredFormValue = (value: unknown): boolean =>
    value !== null && value !== undefined && String(value).trim() !== '';

type PurchaseStepThreeFormState = {
    nombre: string;
    apellido: string;
    email: string;
    repeatEmail: string;
    telefono: string | number;
    numberIdentity?: string | number;
    bookingCode?: string;
    businessName?: string;
    sellerName?: string;
    ticketPersonData?: Array<{
        firstName: string;
        lastName: string;
        email: string;
        numberIdentity?: string | number;
        [key: string]: unknown;
    }>;
    [key: string]: unknown;
};

type PurchaseStepThreeDetalleCompra = {
    requestNumberIdentityField?: boolean;
    numberIdentityDoubleSellValidate: number;
    requestBookingCode?: boolean;
    requestBusinessField?: boolean;
    requestSellerField?: boolean;
    requestTicketPersonData?: boolean;
    customFields: string | null;
};

export const isPurchaseStepThreeFormIncomplete = (
    formState: PurchaseStepThreeFormState,
    formStateError: Record<string, unknown>,
    detalleCompra: PurchaseStepThreeDetalleCompra,
    provinciaSeleccionada: unknown,
    municipioSeleccionado: unknown,
    terminosCondicionesSeleccionado: boolean,
    langValue: string,
    isLoading: boolean,
): boolean => {
    if (isLoading) {
        return true;
    }

    const requiredBaseFields = ['nombre', 'apellido', 'email', 'repeatEmail', 'telefono'] as const;
    for (const field of requiredBaseFields) {
        if (!hasRequiredFormValue(formState[field])) {
            return true;
        }
    }

    if (
        (detalleCompra.requestNumberIdentityField || detalleCompra.numberIdentityDoubleSellValidate > 0)
        && !hasRequiredFormValue(formState.numberIdentity)
    ) {
        return true;
    }

    if (detalleCompra.requestBookingCode && !hasRequiredFormValue(formState.bookingCode)) {
        return true;
    }

    if (detalleCompra.requestBusinessField && !hasRequiredFormValue(formState.businessName)) {
        return true;
    }

    if (detalleCompra.requestSellerField && !hasRequiredFormValue(formState.sellerName)) {
        return true;
    }

    if (detalleCompra.customFields) {
        for (const customField of parseJsonStringToArray(detalleCompra.customFields) as CustomField[]) {
            if (customField.tipo_formulario === 'c' && customField.mandatorio === '1') {
                if (!hasRequiredFormValue(formState[customField.attribute])) {
                    return true;
                }
            }
        }
    }

    // los custom fields 'pe' se muestran aunque requestTicketPersonData sea false,
    // asi que su validacion no puede colgar de esa bandera
    const mandatoryPersonCustomFields = (parseJsonStringToArray(detalleCompra.customFields) as CustomField[])
        .filter((customField) => customField.tipo_formulario === 'pe' && customField.mandatorio === '1');

    if (
        formState.ticketPersonData
        && (detalleCompra.requestTicketPersonData || mandatoryPersonCustomFields.length > 0)
    ) {
        for (const person of formState.ticketPersonData) {
            if (detalleCompra.requestTicketPersonData) {
                if (
                    !hasRequiredFormValue(person.firstName)
                    || !hasRequiredFormValue(person.lastName)
                    || !hasRequiredFormValue(person.email)
                ) {
                    return true;
                }

                if (langValue === 'es_AR' && !hasRequiredFormValue(person.numberIdentity)) {
                    return true;
                }
            }

            for (const customField of mandatoryPersonCustomFields) {
                if (!hasRequiredFormValue(person[customField.attribute])) {
                    return true;
                }
            }
        }
    }

    const formErrorValues = Object.values(formStateError);
    if (formErrorValues.some((value) => value !== '' && typeof value !== 'object')) {
        return true;
    }

    const ticketPersonErrors = formStateError.ticketPersonData;
    if (Array.isArray(ticketPersonErrors)) {
        for (const personErrors of ticketPersonErrors) {
            if (Object.values(personErrors as Record<string, unknown>).some((error) => error !== '')) {
                return true;
            }
        }
    }

    if (provinciaSeleccionada === null) {
        return true;
    }

    if (municipioSeleccionado === null) {
        return true;
    }

    if (langValue === 'es_ES' && !terminosCondicionesSeleccionado) {
        return true;
    }

    return false;
};

export const checkSiteStatus = async (url: string) => {
    try {
        // Tiene que ser una "simple request" de CORS: GET sin headers de autor.
        // Cache-Control y Pragma no estan en la safelist y dispararian un
        // preflight OPTIONS, que la fila redirige igual y nos deja sin respuesta.
        // El no-store alcanza para saltear la cache y no agrega headers propios.
        const response = await fetch(url, {
            method: 'GET',
            cache: 'no-store',
            // sin esto el browser sigue el redirect y nunca vemos el 302 de la fila
            redirect: 'manual',
            // la cookie de la fila vive en el dominio del BFF: sin mandarla el
            // chequeo daria 302 aun teniendo acceso valido
            credentials: 'include'
        });
        // con redirect:'manual' el 302 llega como opaqueredirect (status 0)
        if (response.type === 'opaqueredirect') {
            return 302;
        }
        return response.status;
    } catch (error: any) {
        console.log('Error checking URL status:', error);
        return error?.response?.status;
    }
}
